import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Target, Calendar, TrendingUp, ChevronRight, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { db } from '../../../lib/db';
import { formatDate, getDaysUntil, isOverdue, isDueSoon } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor, getScoreLabel, getScoreColor } from '../../../lib/calculations';
import { useAuthStore } from '../../../store/authStore';

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [appraisals, setAppraisals] = useState([]);
  const [goals, setGoals] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    try {
      const [appraisalsData, goalsData, eventsData] = await Promise.all([
        db.appraisals.toArray(),
        db.goals.toArray(),
        db.calendarEvents.toArray()
      ]);
      setAppraisals(appraisalsData.filter(a => a.userId === user?.id));
      setGoals(goalsData.filter(g => g.userId === user?.id));
      setEvents(eventsData.filter(e => e.participants?.includes(user?.id) || e.createdBy === user?.id));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const myAppraisals = appraisals;
  const pendingAppraisals = myAppraisals.filter(a => a.status !== 'finalized');
  const completedAppraisals = myAppraisals.filter(a => a.status === 'finalized');
  const activeGoals = goals.filter(g => g.status !== 'completed');
  const upcomingEvents = events.slice(0, 3);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'finalized': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'employee_completed': return <Clock className="w-5 h-5 text-amber-500" />;
      default: return <AlertCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back, {user?.profile?.firstName || 'User'}!</h1>
          <p className="text-gray-500 mt-1">Track your performance and goals</p>
        </div>
      </div>

      <Card className="border-0 shadow-sm bg-gradient-to-r from-cyan-50 via-teal-50 to-emerald-50">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-teal-500/25">
              {user?.profile?.firstName?.[0]}{user?.profile?.lastName?.[0]}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">{user?.profile?.firstName} {user?.profile?.lastName}</h2>
              <p className="text-gray-600">{user?.profile?.position}</p>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Department:</span>
                  <Badge variant="secondary" className="bg-white">{user?.profile?.department}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Location:</span>
                  <Badge variant="secondary" className="bg-white">{user?.profile?.location}</Badge>
                </div>
              </div>
            </div>
            <Button variant="outline" className="shrink-0" onClick={() => navigate('/employee/settings')}>Edit Profile</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/employee/appraisals')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active Appraisals</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{pendingAppraisals.length}</p>
                <p className="text-xs text-cyan-600 mt-1">In progress</p>
              </div>
              <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center"><ClipboardList className="w-6 h-6 text-cyan-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/employee/goals')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active Goals</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{activeGoals.length}</p>
                <p className="text-xs text-violet-600 mt-1">To achieve</p>
              </div>
              <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center"><Target className="w-6 h-6 text-violet-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completed Reviews</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{completedAppraisals.length}</p>
                <p className="text-xs text-emerald-600 mt-1">All time</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-emerald-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/employee/calendar')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Upcoming Events</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{upcomingEvents.length}</p>
                <p className="text-xs text-amber-600 mt-1">This week</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center"><Calendar className="w-6 h-6 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">My Appraisals</CardTitle>
              <Button variant="ghost" size="sm" className="text-cyan-600" onClick={() => navigate('/employee/appraisals')}>View All <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {myAppraisals.length > 0 ? myAppraisals.slice(0, 3).map(appraisal => {
                const daysUntil = getDaysUntil(appraisal.dueDate);
                const isAppraisalOverdue = isOverdue(appraisal.dueDate);
                const dueSoon = isDueSoon(appraisal.dueDate, 7);
                return (
                  <div key={appraisal.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:border-cyan-200 hover:bg-cyan-50/30 transition-all cursor-pointer" onClick={() => navigate(`/employee/appraisals/${appraisal.id}`)}>
                    <div className="flex items-center gap-4">
                      {getStatusIcon(appraisal.status)}
                      <div>
                        <p className="font-medium text-gray-900">{appraisal.title}</p>
                        <p className="text-sm text-gray-500 capitalize">{appraisal.type} Review</p>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge className={getStatusBadgeColor(appraisal.status)} variant="secondary">{formatStatus(appraisal.status)}</Badge>
                          {appraisal.status !== 'finalized' && (
                            <span className={`text-xs ${isAppraisalOverdue ? 'text-rose-600' : dueSoon ? 'text-amber-600' : 'text-gray-400'}`}>
                              {isAppraisalOverdue ? `Overdue by ${Math.abs(daysUntil)} days` : `Due in ${daysUntil} days`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 justify-end mb-2">
                        <span className="text-sm text-gray-500">Progress:</span>
                        <span className="text-sm font-medium text-gray-900">{appraisal.progress?.overallPercent || 0}%</span>
                      </div>
                      <Progress value={appraisal.progress?.overallPercent || 0} className="w-32 h-2" />
                    </div>
                  </div>
                );
              }) : <p className="text-sm text-gray-500 text-center py-4">No appraisals assigned yet</p>}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2"><Target className="w-5 h-5 text-violet-500" />Active Goals</CardTitle>
                <Badge variant="secondary">{activeGoals.length}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {activeGoals.length > 0 ? activeGoals.slice(0, 3).map(goal => (
                <div key={goal.id} className="p-3 bg-violet-50/50 rounded-lg border border-violet-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm text-gray-900">{goal.title}</p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{goal.description}</p>
                    </div>
                    <Badge className={`text-xs ${goal.priority === 'high' ? 'bg-rose-100 text-rose-700' : goal.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{goal.priority}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <Badge className={`text-xs ${goal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{goal.status?.replace('_', ' ')}</Badge>
                    <span className="text-xs text-gray-400">Due: {formatDate(goal.dueDate)}</span>
                  </div>
                </div>
              )) : <p className="text-sm text-gray-500 text-center py-4">No active goals</p>}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-500" />Upcoming</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {upcomingEvents.length > 0 ? upcomingEvents.map(event => (
                <div key={event.id} className="p-3 bg-amber-50/50 rounded-lg border border-amber-100">
                  <p className="font-medium text-sm text-gray-900">{event.title}</p>
                  <p className="text-xs text-gray-500 mt-1">{event.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary" className="text-xs bg-white">{event.type}</Badge>
                    <span className="text-xs text-gray-400">{formatDate(event.startDate)}</span>
                  </div>
                </div>
              )) : <p className="text-sm text-gray-500 text-center py-4">No upcoming events</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      {completedAppraisals.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-500" />Performance History</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {completedAppraisals.map(appraisal => (
                <div key={appraisal.id} className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                  <p className="font-medium text-gray-900">{appraisal.title}</p>
                  <p className="text-sm text-gray-500">Completed: {formatDate(appraisal.finalizedAt || appraisal.updatedAt)}</p>
                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Final Score</p>
                      <p className={`text-2xl font-bold ${getScoreColor(appraisal.scores?.finalWeightedScore)}`}>{appraisal.scores?.finalWeightedScore?.toFixed(1) || 'N/A'}</p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">{getScoreLabel(appraisal.scores?.finalWeightedScore)}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
